import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../store";

import { requestSymbol } from "./volatilityAnalysisActions";
import VolatilityAnalysis from "./VolatilityAnalysis";
import { constant } from "lodash-es";

// export interface AuthState {
//   authentication: object;
// }

// const initialState: AuthState = {

const sortByExpirationDateString = (a: string | null, b: string | null) => {
  //mirrors sort in fut list

  const dateA = a ? new Date(a) : null;
  const dateB = b ? new Date(b) : null;

  // Handle sorting logic with nulls
  if (dateA === null && dateB === null) return 0;
  if (dateA === null) return 1;
  if (dateB === null) return -1;

  return dateA.getTime() - dateB.getTime();
};

const initalizeSelections = (
  newData: VolatilityAnalysisData,
  futuresData?: {
    [symbol: string]: {
      expiration_date: string;
      name: string;
      historical_data: {
        [date: string]: {
          open: number;
          high: number;
          low: number;
          close: number;
        };
      };
    };
  },
) => {
  const selections: selectionState = {};

  const newSelections: Selections = {
    selectedFutures: {},
    selectedOptions: {},
  };

  if (futuresData) {
    for (const fut in futuresData) {
      if (!newSelections.selectedFutures[fut]) {
        newSelections.selectedFutures[fut] = {
          checked: false,
          expirationDate: futuresData[fut].expiration_date,
          name: futuresData[fut].name,
        };
      }
    }
  }

  for (const date in newData) {
    for (const fut in newData[date]) {
      if (!newSelections.selectedFutures[fut]) {
        newSelections.selectedFutures[fut] = {
          checked: false,
          expirationDate: newData[date][fut].futuresExpirationDate,
          name: newData[date][fut].name,
        };
      }
      Object.keys(newData[date][fut].options).forEach((option) => {
        if (!newSelections.selectedOptions[option]) {
          newSelections.selectedOptions[option] = {
            checked: false,
            visible: false,
            name: newData[date][fut].options[option].meta.name,
            expirationDate:
              newData[date][fut].options[option].meta.expirationDate,
            underlying: fut,
          };
        }
      });
    }
  }
  //selection state is built, next add inital selections

  const lastestDate = Object.keys(newData)
    .filter((date) =>
      Object.values(newData[date]).some(
        (fut) =>
          fut.options &&
          Object.keys(fut.options).length > 0 &&
          fut.futuresExpirationDate,
      ),
    )
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

  //Get the future with the latest expiration date within the opiton data selection window
  const latestFuture = Object.entries(newData[lastestDate])
    .filter(
      ([future, meta]) =>
        new Date(meta.futuresExpirationDate) > new Date(lastestDate) &&
        Object.keys(meta.options).length > 0,
    )
    .sort(
      ([a, aMeta], [b, bMeta]) =>
        -1 *
        (new Date(bMeta.futuresExpirationDate).getTime() -
          new Date(aMeta.futuresExpirationDate).getTime()),
    )[0][0];
  newSelections.selectedFutures[latestFuture].checked = true;

  Object.entries(newSelections.selectedOptions)
    .filter(
      ([symbol, meta]) =>
        meta.underlying === latestFuture &&
        symbol.includes(latestFuture.slice(0, -3)),
    )
    .forEach(([, meta]) => (meta.checked = true));

  return newSelections;
};

export const enum Status {
  LOADING = "LOADING",
  IDLE = "IDLE",
}

export type VolatilityAnalysisData = {
  [index: string]: {
    [index: string]: {
      open: number;
      high: number;
      low: number;
      close: number;
      futuresExpirationDate: string;
      name: string | null;
      options: {
        [index: string]: {
          meta: {
            expirationDate: string;
            name: string;
            deltaStrikeRange: { Calls: number; Puts: number };
          };
          ivol: {
            Calls: { [index: string]: number };
            Puts: { [index: string]: number };
          };
        };
      };
    };
  };
};

export interface selectionState {
  [future: string]: {
    checked: boolean;
    expirationDate: string;
    options: {
      [option_contract: string]: { checked: boolean; expirationDate: string };
    };
  };
}
interface Selections {
  selectedFutures: FuturesSelections;
  selectedOptions: OptionsSelections;
}

export interface FuturesSelections {
  [future: string]: {
    checked: boolean;
    expirationDate: string;
    name: string | null;
  };
}

export interface OptionsSelections {
  [option: string]: {
    visible: boolean;
    checked: boolean;
    expirationDate: string;
    underlying: string;
    name: string | null;
  };
}

// interface VolatilityAnalysisState{
//   data : VolatilityAnalysisData;
//   status: Status;
//   selectedFutures: FuturesSelections;
//   selectedOptions: OptionsSelections;
// }

const initialState = {
  data: {},
  status: Status.IDLE,
  selectedFutures: {},
  selectedOptions: {},
  portalFuturesData: {},
};

export const volatilityAnalysisSlice = createSlice({
  name: "volatilityAnalysis",
  initialState,
  reducers: {
    reset: (state) => initialState,
    // setVolatilityAnalysisData: (state, action) => {
    //   console.debug("state:");
    //   console.debug(state);
    //   console.debug("action:");
    //   console.debug(action);
    //   // state.profile.createdAt = action.payload.created_at;
    //   state.data = action.payload;
    // },
    changeSelectedFutures: (state, action: PayloadAction<string>) => {
      const fut_key = action.payload;
      const selectedFutures: FuturesSelections = state.selectedFutures;
      const selectedOptions: OptionsSelections = state.selectedOptions;

      if (selectedFutures[fut_key]) {
        selectedFutures[fut_key].checked = !selectedFutures[fut_key].checked;

        Object.entries(selectedOptions)
          .filter(([, meta]) => meta.underlying === fut_key)
          .map(([, meta]) => (meta.visible = !meta.visible));
      } else {
        console.warn(`The key '${fut_key}' does not exist in selectedFutures.`);
      }
    },
    changeSelectedOptions: (state, action: PayloadAction<string>) => {
      const opt_key = action.payload;
      const selectedOptions: OptionsSelections = state.selectedOptions;

      if (selectedOptions[opt_key]) {
        selectedOptions[opt_key].checked = !selectedOptions[opt_key].checked;
      } else {
        console.warn(`The key '${opt_key}' does not exist in selectedOptions.`);
      }
    },
    toggleSelectAllFutures: (state) => {
      const selectedFutures: FuturesSelections = state.selectedFutures;
      const selectedOptions: OptionsSelections = state.selectedOptions;

      const allChecked = Object.values(selectedFutures).every(
        (item) => item.checked,
      );

      Object.values(selectedFutures).forEach((item) => {
        item.checked = !allChecked;
      });

      Object.values(selectedOptions).forEach((item) => {
        item.visible = !allChecked;
      });
    },
    toggleSelectAllOptions: (state) => {
      const selectedOptions: OptionsSelections = state.selectedOptions;

      const allChecked = Object.values(selectedOptions).every(
        (item) => !item.visible || item.checked,
      );

      Object.values(selectedOptions).forEach((item) => {
        if (item.visible) {
          item.checked = !allChecked;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestSymbol.pending, (state, action) => {
        state.status = Status.LOADING;
      })
      .addCase(requestSymbol.fulfilled, (state, action) => {
        console.debug("state:");
        console.debug(state);
        console.debug("action:");
        console.debug(action);
        state.data = action.payload.volatilityAnalysisData;
        state.portalFuturesData = action.payload.portalFuturesData;
        state.status = Status.IDLE;
        const selections = initalizeSelections(
          state.data,
          state.portalFuturesData,
        );
        state.selectedOptions = selections.selectedOptions;
        state.selectedFutures = selections.selectedFutures;
      })
      .addCase(requestSymbol.rejected, (state, action) => {
        state.status = Status.IDLE;
        console.debug("requestSymbol.rejected action:");
        console.debug(action);
        state.status = Status.IDLE;
      });
  },
});

const { actions, reducer } = volatilityAnalysisSlice;

// actions automatically generated by redux
export const {
  reset,
  changeSelectedFutures,
  changeSelectedOptions,
  toggleSelectAllOptions,
  toggleSelectAllFutures,
} = actions;

export const selectVolatilityAnalysis = (state: RootState) =>
  state.volatilityAnalysis;

// Selector to check if data exists in the store
export const selectHasData = (state: RootState) => {
  const data = state.volatilityAnalysis.data;
  return Object.keys(data).length > 0;
};

// Selector to check if store is initialized with data (IDLE + has data)
export const selectIsInitialized = (state: RootState) => {
  const { status, data } = state.volatilityAnalysis;
  return status === Status.IDLE && Object.keys(data).length > 0;
};

// Selector to check if store is empty (IDLE + no data)
export const selectIsEmpty = (state: RootState) => {
  const { status, data } = state.volatilityAnalysis;
  return status === Status.IDLE && Object.keys(data).length === 0;
};

export default reducer;

// interface futuresState {
//   [index: string]: {
//     isChecked: boolean;
//     isVisible: boolean;
//   };
// }

// const initialFutState: futuresState = {};

// const futuresStateSlice = createSlice({
//   name: "futures",
//   initialState: initialFutState,
//   reducers: {
//     // Add a new future with a given key
//     addFuture: (
//       state,
//       action: PayloadAction<{
//         key: string;
//         isChecked: boolean;
//         isVisible: boolean;
//       }>,
//     ) => {
//       const { key, isChecked, isVisible } = action.payload;
//       if (!state[key]) {
//         state[key] = { isChecked, isVisible };
//       }
//     },

//     // Toggle the isChecked property of a specific future (by key)
//     toggleChecked: (state, action: PayloadAction<string>) => {
//       const future = state[action.payload];
//       if (future) {
//         future.isChecked = !future.isChecked;
//       }
//     },

//     // Toggle the isVisible property of a specific future (by key)
//     toggleVisibility: (state, action: PayloadAction<string>) => {
//       const future = state[action.payload];
//       if (future) {
//         future.isVisible = !future.isVisible;
//       }
//     },

//     // Remove a future by key
//     removeFuture: (state, action: PayloadAction<string>) => {
//       const key = action.payload;
//       if (state[key]) {
//         delete state[key];
//       }
//     },

//     // Update both isChecked and isVisible for a specific future by key
//     updateFuture: (
//       state,
//       action: PayloadAction<{
//         key: string;
//         isChecked: boolean;
//         isVisible: boolean;
//       }>,
//     ) => {
//       const { key, isChecked, isVisible } = action.payload;
//       if (state[key]) {
//         state[key].isChecked = isChecked;
//         state[key].isVisible = isVisible;
//       }
//     },
//   },
// });
